/**
 * The player's own withdrawal (R34/D114) — `never mind` sets the parked
 * question aside, and the chip's dismiss submits the same words.
 *
 * @remarks
 * D79 modeled questions as state that survives until answered, replaced, or
 * left behind — and deliberately kept UI dismissal OUT of the model, because
 * a state-changing gesture outside the input stream would break `(bundle,
 * seed, commands)` reproducibility. Calder's §7.1 finding (2026-08-28) showed
 * what that honest constraint produced: a chip that LOOKS dismissible and
 * lies — hidden in presentation, standing in the engine, answering every
 * later miss in its terms. The resolution keeps both truths: withdrawal is a
 * real INPUT (`never mind`), so replays carry it like any other words, the
 * transcript records it, and the chip's ✕ submits it through the same door
 * the keyboard uses. The presentation-only dismissal flag is deleted — one
 * mechanism, visible everywhere it acts.
 *
 * The directive is the WHOLE input, exactly: `never mind`, `nevermind`,
 * `forget it` (the standard vocabulary's `nevermind` intent — packs rename
 * it by aliasing, the usual way). A chain containing it (`never mind and go
 * north`) is not a withdrawal — it falls through to the ordinary pipeline,
 * where the engine has no action for the token and teaches honestly.
 *
 * No turn is consumed on either arm: withdrawal is conversation management,
 * not a world action — the same reading D27 gave reactions, whose outcome
 * arm this shares.
 *
 * @packageDocumentation
 */

import type { RuntimeState, RuntimeTranscriptEntry } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { PlayerInputOutcome } from './process-player-input.js';

import { bumpResponseUsage, speakSlot } from '../defaults/default-responses.js';
import { terminalEndingOf } from '../narrative/session-ended.js';
import { WITHDRAWAL_INTENT_TOKEN } from './chrome-directive-tokens.js';
import { wholeInputIntentOf } from './chrome-directives.js';
import { clearQuestion } from './pending-question.js';
import { inputNamespaceCursor, stampNarrativeLines } from './transcript-cursor.js';

/**
 * Whether this input, whole, spells the withdrawal directive — and the world
 * is still open to hear it (the R11 terminal gate outranks the directive, so
 * a closed world's inputs all fall through to the ordinary pipeline, whose
 * gate declines them after the echo commit).
 *
 * @remarks
 * Matched against the compiled lexicon rather than a literal list, so packs
 * that alias the `nevermind` intent get their own words for free. Exact-whole
 * match only: a single token checks `intentsByToken`, several check the
 * multiword aliases keyed on the first — the same two tables canonicalization
 * reads, consulted without a parse because the directive has no grammar.
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this input (read for the terminal gate)
 * @param rawInput - Exactly what the player typed
 * @returns True when the input is the withdrawal directive and nothing else
 */
export const isWithdrawalDirective = (bundle: EngineGameBundle, state: RuntimeState, rawInput: string): boolean =>
	terminalEndingOf(bundle, state) === undefined && wholeInputIntentOf(bundle, rawInput) === WITHDRAWAL_INTENT_TOKEN;

/**
 * Commit the withdrawal: echo, history, the one line, and — when a question
 * is parked — its closure.
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this input
 * @param rawInput - Exactly what the player typed (echoed verbatim, D23)
 * @returns A committed reaction-arm outcome; no turn consumed on either arm
 */
export const withdrawParkedQuestion = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	rawInput: string,
): PlayerInputOutcome => {
	const parkedQuestion = state.session.pendingQuestion;
	const inputOrdinal = state.session.commandHistory.length + 1;
	const echo = stampNarrativeLines(
		bundle,
		state,
		inputNamespaceCursor(inputOrdinal, 0, state),
		[{ priority: 'standard', source: 'player', text: rawInput }],
		'player_input',
	);
	const spoken = speakSlot(bundle, state, parkedQuestion === undefined ? 'noQuestionToWithdraw' : 'questionSetAside');
	const spokenContext =
		parkedQuestion === undefined
			? 'question_set_aside:none'
			: `question_set_aside:${String(parkedQuestion.askedAtInputOrdinal)}`;
	const stamped = stampNarrativeLines(bundle, state, echo.cursor, [spoken.line], spokenContext);
	const entries: readonly RuntimeTranscriptEntry[] = [...echo.entries, ...stamped.entries];

	return {
		corrections: [],
		nextState: clearQuestion({
			...state,
			session: {
				...state.session,
				commandHistory: [...state.session.commandHistory, rawInput],
				responseUsage: bumpResponseUsage(state.session.responseUsage, [spoken.slot]),
				transcript: [...state.session.transcript, ...entries],
			},
		}),
		transcriptEntries: entries,
		type: 'reaction',
	};
};
